import { Module } from 'vuex';

import { RootState, Nullable } from 'frontend/types';
import { EditorStatusTuple } from 'frontend/types/lessons/editor';
import { ErrorsDict } from 'frontend/types/lessons/requests';
import { apiResolve } from 'frontend/router';
import { UserLibrary, UserLibrarySummary } from 'frontend/types/lessons/userLibraries';
import { ElmerError } from 'frontend/types/elmer';
import { getJson } from 'frontend/utils/ajax';

interface UserLibraryState {
  userLibrary?: UserLibrary;
  libraryVersions: UserLibrarySummary[];
  usableLibraries: UserLibrarySummary[];
  requestErrors: string[];
  elmerErrors: Nullable<ElmerError>;
  editorStatus: Nullable<EditorStatusTuple>;
}

interface UserLibrariesBackendOutput {
  user_library?: UserLibrary;
  library_versions?: UserLibrarySummary[];
  usable_libraries?: UserLibrarySummary[];
}

function getInitialState(): UserLibraryState {
  return {
    userLibrary: undefined,
    libraryVersions: [],
    usableLibraries: [],
    requestErrors: [],
    elmerErrors: null,
    editorStatus: null,
  };
}

const userLibraries: Module<UserLibraryState, RootState> = {
  namespaced: true,
  state: getInitialState(),

  mutations: {
    setValue(state: UserLibraryState, backend: UserLibrariesBackendOutput) {
      const initialState = getInitialState();
      state.userLibrary = backend.user_library || initialState.userLibrary;
      state.libraryVersions = backend.library_versions || initialState.libraryVersions;
      state.usableLibraries = backend.usable_libraries || initialState.usableLibraries;
    },
    setErrors(state: UserLibraryState, value: ErrorsDict) {
      state.requestErrors = value.requestErrors;
      state.elmerErrors = value.elmerErrors;
    },
    setStatus(state: UserLibraryState, editorStatus: EditorStatusTuple) {
      state.editorStatus = editorStatus;
    },
    setUserLibrary(state: UserLibraryState, library: UserLibrary) {
      state.userLibrary = library;
    },
  },

  actions: {
    async loadUsableElmPackages({ commit }): Promise<any> {
      const response = await getJson(apiResolve({ name: 'elm_packages_list' }).href);
      commit('setValue', { usable_libraries: response.results.packages });
    },
    setStatus({ commit }, editorStatus: EditorStatusTuple) {
      commit('setStatus', editorStatus);
    },
    clearUserLibrary({ commit }) {
      commit('setUserLibrary', getInitialState().userLibrary);
    },
  },
  getters: {
    editorStatusText(state: UserLibraryState): string | null {
      return state.editorStatus ? state.editorStatus[0] : null;
    },
    editorStatusType(state: UserLibraryState): string | null {
      return state.editorStatus ? state.editorStatus[1].toLowerCase() : null;
    },
    libraryVersions(state: UserLibraryState): UserLibrarySummary[] {
      return state.libraryVersions;
    },
    usableLibraries(state: UserLibraryState): UserLibrarySummary[] {
      return state.usableLibraries;
    },
  },
};

export default userLibraries;
